import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Copy, RefreshCw, Lock, Smartphone } from "lucide-react";

const input = (t) => ({ background: t.bgSecondary, border: `1px solid ${t.borderColor}`, color: t.textPrimary });
const detail = (e, fallback) => e?.response?.data?.detail || fallback;
const isCodeReady = (v) => { const c = v.replace(/\s/g, ""); return /^\d{6}$/.test(c) || /^[a-z0-9]{5}-?[a-z0-9]{5}$/i.test(c); };

function CodeInput({ value, onChange, t, testId, placeholder = "000000" }) {
  return (
    <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={value}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9 ]/g, ""))}
      className="w-full px-3 py-2 text-sm rounded-lg outline-none tracking-[0.3em]" style={input(t)}
      placeholder={placeholder} data-testid={testId} />
  );
}

// Accepts a 6-digit authenticator code or a recovery code (xxxxx-xxxxx).
function SecondFactorInput({ value, onChange, t, testId }) {
  return (
    <input type="text" autoComplete="one-time-code" maxLength={11} value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 text-sm rounded-lg outline-none tracking-[0.2em]" style={input(t)}
      placeholder="Authenticator or recovery code" data-testid={testId} />
  );
}

// Shown once after enrolment / regeneration. Requires an explicit "saved" tick before continuing.
export function RecoveryCodes({ codes, onDone, t }) {
  const [saved, setSaved] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(codes.join("\n")); toast.success("Recovery codes copied"); }
    catch { toast.error("Couldn't copy — write them down instead"); }
  };
  return (
    <div className="space-y-3" data-testid="twofa-recovery-codes">
      <p className="text-xs" style={{ color: t.textMuted }}>
        Each code works once if you lose your authenticator. They won't be shown again — keep them somewhere safe.
      </p>
      <div className="grid grid-cols-2 gap-1.5 p-3 rounded-lg font-mono text-sm" style={{ background: t.bgSecondary, color: t.textPrimary, border: `1px solid ${t.borderColor}` }}>
        {codes.map((c) => <span key={c} data-testid="twofa-recovery-code">{c}</span>)}
      </div>
      <button type="button" onClick={copy} className="flex items-center gap-1.5 text-xs hover:underline" style={{ color: t.accent }} data-testid="twofa-copy-codes">
        <Copy className="h-3 w-3" /> Copy all
      </button>
      <label className="flex items-center gap-2 text-sm cursor-pointer select-none" style={{ color: t.textPrimary }}>
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-testid="twofa-saved-checkbox" />
        I've saved these codes
      </label>
      <button type="button" disabled={!saved} onClick={onDone}
        className="px-4 py-2 text-sm font-semibold rounded-lg disabled:opacity-50" style={{ background: t.accent, color: t.bgPrimary }} data-testid="twofa-codes-done-btn">
        Continue
      </button>
    </div>
  );
}

// Enrolment: [change: current code →] setup → QR + manual key + otpauth link → confirm code → recovery codes.
export function TwoFactorEnrol({ onEnrolled, onCancel, change = false, t }) {
  const [setup, setSetup] = useState(null);
  const [currentCode, setCurrentCode] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);

  const start = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try { setSetup(await api.twoFactorSetup(change ? currentCode.trim() : undefined)); }
    catch (err) { toast.error(detail(err, change ? "That code didn't match" : "Couldn't start enrolment")); }
    setBusy(false);
  };
  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const res = await api.twoFactorConfirm(code); setCodes(res.recovery_codes); }
    catch (err) { toast.error(detail(err, "That code didn't match — try the next one")); }
    setBusy(false);
  };

  if (codes) return <RecoveryCodes codes={codes} onDone={onEnrolled} t={t} />;
  if (!setup && change) {
    return (
      <form onSubmit={start} className="space-y-2" data-testid="twofa-change-form">
        <p className="text-xs" style={{ color: t.textMuted }}>
          Confirm it's you with a code from your current app (or a recovery code). Your old app and recovery codes stop working once the new one is confirmed.
        </p>
        <SecondFactorInput value={currentCode} onChange={setCurrentCode} t={t} testId="twofa-change-current-code" />
        <div className="flex items-center gap-2">
          <button type="submit" disabled={busy || !isCodeReady(currentCode)}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg disabled:opacity-50" style={{ background: t.accent, color: t.bgPrimary }} data-testid="twofa-change-continue">
            {busy ? "Checking…" : "Continue"}
          </button>
          <button type="button" onClick={onCancel} className="text-xs hover:underline" style={{ color: t.textMuted }}>Cancel</button>
        </div>
      </form>
    );
  }
  if (!setup) {
    return (
      <button type="button" onClick={start} disabled={busy}
        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg disabled:opacity-50" style={{ background: t.accent, color: t.bgPrimary }} data-testid="twofa-enable-btn">
        <ShieldCheck className="h-3.5 w-3.5" /> {busy ? "Preparing…" : "Enable two-factor authentication"}
      </button>
    );
  }
  return (
    <form onSubmit={confirm} className="space-y-3" data-testid="twofa-enrol-card">
      <p className="text-xs" style={{ color: t.textMuted }}>
        Scan this with your authenticator app (Google Authenticator, Authy, 1Password…) or enter the key manually, then type the code it shows.
      </p>
      <div className="inline-block rounded-lg bg-white p-1" data-testid="twofa-qr">
        <QRCodeSVG value={setup.otpauth_uri} size={180} level="M" marginSize={2} bgColor="#FFFFFF" fgColor="#000000" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: t.textMuted }}>Manual entry key</div>
        <code className="block p-2.5 rounded-lg font-mono text-sm break-all select-all" style={{ background: t.bgSecondary, color: t.textPrimary, border: `1px solid ${t.borderColor}` }} data-testid="twofa-manual-key">
          {setup.secret_manual.match(/.{1,4}/g).join(" ")}
        </code>
      </div>
      <a href={setup.otpauth_uri} className="block text-xs hover:underline break-all" style={{ color: t.accent }} data-testid="twofa-otpauth-link">
        Open in authenticator app (otpauth:// link)
      </a>
      <CodeInput value={code} onChange={setCode} t={t} testId="twofa-confirm-code" />
      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy || code.replace(/\s/g, "").length !== 6}
          className="px-4 py-2 text-sm font-semibold rounded-lg disabled:opacity-50" style={{ background: t.accent, color: t.bgPrimary }} data-testid="twofa-confirm-btn">
          {busy ? "Checking…" : "Confirm"}
        </button>
        <button type="button" onClick={() => { setSetup(null); onCancel?.(); }} className="text-xs hover:underline" style={{ color: t.textMuted }} data-testid="twofa-enrol-cancel">Cancel</button>
      </div>
    </form>
  );
}

function DisablePanel({ onDisabled, t }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.twoFactorDisable(password, code); toast.success("Two-factor authentication turned off"); onDisabled(); }
    catch (err) { toast.error(detail(err, "Couldn't turn off two-factor authentication")); }
    setBusy(false);
  };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="flex items-center gap-1.5 text-xs hover:underline" style={{ color: "#EF4444" }} data-testid="twofa-disable-btn">
        <ShieldOff className="h-3 w-3" /> Turn off two-factor authentication
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-2" data-testid="twofa-disable-form">
      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Current password" required
        className="w-full px-3 py-2 text-sm rounded-lg outline-none" style={input(t)} data-testid="twofa-disable-password" />
      <SecondFactorInput value={code} onChange={setCode} t={t} testId="twofa-disable-code" />
      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy || !password || !isCodeReady(code)}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg disabled:opacity-50" style={{ background: "#EF4444", color: "#fff" }} data-testid="twofa-disable-confirm">
          {busy ? "Working…" : "Turn off"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="text-xs hover:underline" style={{ color: t.textMuted }}>Cancel</button>
      </div>
    </form>
  );
}

function RegeneratePanel({ t }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const res = await api.twoFactorRegenerate(code); setCodes(res.recovery_codes); setCode(""); }
    catch (err) { toast.error(detail(err, "Couldn't regenerate recovery codes")); }
    setBusy(false);
  };
  if (codes) return <RecoveryCodes codes={codes} onDone={() => { setCodes(null); setOpen(false); }} t={t} />;
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="flex items-center gap-1.5 text-xs hover:underline" style={{ color: t.accent }} data-testid="twofa-regenerate-btn">
        <RefreshCw className="h-3 w-3" /> Regenerate recovery codes
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-2" data-testid="twofa-regenerate-form">
      <p className="text-xs" style={{ color: t.textMuted }}>Enter a current authenticator code (or an unused recovery code). Your old recovery codes stop working immediately.</p>
      <SecondFactorInput value={code} onChange={setCode} t={t} testId="twofa-regenerate-code" />
      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy || !isCodeReady(code)}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg disabled:opacity-50" style={{ background: t.accent, color: t.bgPrimary }} data-testid="twofa-regenerate-confirm">
          {busy ? "Working…" : "Generate new codes"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="text-xs hover:underline" style={{ color: t.textMuted }}>Cancel</button>
      </div>
    </form>
  );
}

export default function TwoFactorCard({ t }) {
  const { user, refreshUser } = useAuth();
  const [changing, setChanging] = useState(false);
  const enabled = !!user?.two_factor_enabled;
  const isOwner = !!user?.isShadowOwner;

  const finishChange = async () => { setChanging(false); await refreshUser(); toast.success("Authenticator app updated"); };

  return (
    <div className="pt-4 space-y-3" style={{ borderTop: `1px solid ${t.borderColor}` }} data-testid="twofa-card">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold" style={{ color: t.textPrimary }}>Two-factor authentication</h3>
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" data-testid="twofa-status"
          style={{ background: enabled ? "#10B98120" : `${t.textMuted}20`, color: enabled ? "#10B981" : t.textMuted }}>
          {enabled ? "ON" : "OFF"}
        </span>
      </div>
      {!enabled && (
        <>
          <p className="text-xs" style={{ color: t.textMuted }}>A second code from your phone each time you sign in. {isOwner ? "Required for the owner account." : "Optional, but a good idea."}</p>
          <TwoFactorEnrol onEnrolled={refreshUser} t={t} />
        </>
      )}
      {enabled && changing && <TwoFactorEnrol change onEnrolled={finishChange} onCancel={() => setChanging(false)} t={t} />}
      {enabled && !changing && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <RegeneratePanel t={t} />
            <button type="button" onClick={() => setChanging(true)} className="flex items-center gap-1.5 text-xs hover:underline" style={{ color: t.accent }} data-testid="twofa-change-btn">
              <Smartphone className="h-3 w-3" /> Change authenticator app
            </button>
          </div>
          {isOwner ? (
            <div className="flex items-center gap-1.5 text-xs opacity-60 cursor-not-allowed" style={{ color: t.textMuted }} data-testid="twofa-disable-locked">
              <Lock className="h-3 w-3" /> Turn off two-factor authentication — Required for the owner account
            </div>
          ) : (
            <DisablePanel onDisabled={refreshUser} t={t} />
          )}
        </div>
      )}
    </div>
  );
}
